// ============================================================================
// Limites (refonte, lot 6.58, moteur v54) — le calcul, sans la feuille.
// ============================================================================
// Une limite se cherche en un POINT : +∞, −∞ (fonction), +∞ pour une suite (variable n,
// entière), ou un réel a, à droite (a⁺), à gauche (a⁻) ou des deux côtés.
//
// Trois évaluations :
//
//   `knownLimit`  ce qu'un élève de Terminale CONCLUT d'une écriture : limites de
//                 référence, opérations (somme, produit, quotient, composée, puissance),
//                 continuité, et deux théorèmes nommés — les croissances comparées (un
//                 produit de puissances de x, d'exponentielles et de logarithmes dont les
//                 tendances s'opposent) et le nombre dérivé ((f(x) − f(a)) ÷ (x − a) en a).
//                 Une forme indéterminée (∞ − ∞, 0 × ∞, ∞ ÷ ∞, 0 ÷ 0, 1^∞) n'est PAS levée.
//   `trueLimit`   la vraie limite, quand on sait la trouver : la précédente, puis la limite
//                 EXACTE d'une fraction rationnelle, puis le terme dominant (sommes de
//                 puissances, d'exponentielles et de logarithmes), puis une évaluation
//                 numérique prudente (qui ne fait que confirmer ou réfuter).
//   `sameNear`    deux écritures égales PRÈS du point (formes exactes, sinon valeurs).
//
// Les valeurs finies sont des flottants (comparées à 10⁻⁹ près) ; une limite nulle porte
// son CÔTÉ (0⁺ : par valeurs positives), qui décide de 1 ÷ u.
//
// Moteur v56 (lot 6.61) : arctan EN RADIANS (limites ±π ÷ 2, continuité, nombre dérivé),
// sin et cos d'une expression sans limite restent bornés (x sin(1 ÷ x) en 0), et les deux
// théorèmes s'appliquent à une COMPOSÉE : sin(1 ÷ n) ÷ (1 ÷ n) (X = 1 ÷ n → 0, puis
// sin(X) ÷ X → 1), ln(x²) ÷ x² (X = x², puis ln(X) ÷ X → 0). Un point marqué `v54` garde
// le comportement du moteur v54 (sert à savoir si une feuille a besoin du v56).
// ============================================================================

import * as Q from './exact';
import { seriesLimitOf } from './series';
import { compareTerms, evalFloat, radianArcs, rfToTerm, toRatFn, type Poly } from './normal';
import { derive } from './calculus';
import { canon, num, substitute, sym, symbolsOf, termKey, type Term } from './term';

/** côté d'approche d'une limite finie : 1 par valeurs supérieures, −1 inférieures, 0 inconnu */
export type Side = 1 | -1 | 0;

export type LimVal =
  | { k: 'fin'; v: number; side: Side }
  | { k: 'inf'; s: 1 | -1 }
  /** bornée sans limite : cos(x) en +∞, (−1)ⁿ */
  | { k: 'bnd' }
  /** pas de limite : 1 ÷ x en 0 des deux côtés, (−2)ⁿ */
  | { k: 'none' };

export type FIForm = '∞ − ∞' | '0 × ∞' | '∞ ÷ ∞' | '0 ÷ 0' | '1^∞' | '0⁰' | '∞⁰';

export type LimRes = LimVal | { k: 'fi'; form: FIForm } | { k: 'unknown' };

/** le point : `inf` = ±1 (±∞) ou 0 (réel `a`, côté `side`) ; `seq` : suite (n entier) */
export interface LimPoint {
  x: string;
  inf: 1 | -1 | 0;
  a: number;
  side: Side;
  seq: boolean;
  /** comportement du moteur v54 (sans les ajouts du v56) */
  v54?: boolean;
  /** v58 : la feuille écrit des développements limités (limite en 0 par les DL) */
  dl?: boolean;
}

export const fin = (v: number, side: Side = 0): LimVal => ({ k: 'fin', v, side });
export const inf = (s: 1 | -1): LimVal => ({ k: 'inf', s });
const UNKNOWN: LimRes = { k: 'unknown' };
const fi = (form: FIForm): LimRes => ({ k: 'fi', form });

export type DetVal = Extract<LimVal, { k: 'fin' } | { k: 'inf' }>;
export const isDet = (r: LimRes): r is DetVal => r.k === 'fin' || r.k === 'inf';
export const isZero = (r: LimRes): boolean => r.k === 'fin' && Math.abs(r.v) < 1e-12;

export function closeNum(a: number, b: number): boolean {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b));
}

const sgn = (v: number): -1 | 0 | 1 => (Math.abs(v) < 1e-12 ? 0 : v > 0 ? 1 : -1);

/* ------------------------------ le point ------------------------------ */

export class LimitSyntaxError extends Error {}

/** « +∞ », « −∞ », « 2 », « 2⁺ », « 0⁻ », « −1⁺ », « 1,5 » */
export const POINT_RE = /^([+−]∞|−?\d+(,\d+)?[⁺⁻]?)$/;

export function parsePoint(text: string, x: string, seq: boolean): LimPoint {
  const t = text.trim().replace(/^-/, '−');
  if (!POINT_RE.test(t)) throw new LimitSyntaxError(`point illisible : « ${text} »`);
  if (t === '+∞') return { x, inf: 1, a: 0, side: 0, seq };
  if (t === '−∞') {
    if (seq) throw new LimitSyntaxError('une suite tend vers +∞ seulement');
    return { x, inf: -1, a: 0, side: 0, seq };
  }
  if (seq) throw new LimitSyntaxError('une suite se regarde en +∞');
  const side: Side = t.endsWith('⁺') ? 1 : t.endsWith('⁻') ? -1 : 0;
  const body = side ? t.slice(0, -1) : t;
  const a = Number(body.replace('−', '-').replace(',', '.'));
  if (!Number.isFinite(a)) throw new LimitSyntaxError(`point illisible : « ${text} »`);
  return { x, inf: 0, a, side, seq };
}

/* ------------------------------ écritures ------------------------------ */

/** une limite comme elle s'écrit sur la feuille : « +∞ », « 0⁺ », « 3 », « 1/2 » */
export function limValText(v: LimVal): string {
  switch (v.k) {
    case 'inf':
      return v.s > 0 ? '+∞' : '−∞';
    case 'fin': {
      const base = numText(v.v);
      return v.side && Math.abs(v.v) < 1e-12 ? `0${v.side > 0 ? '⁺' : '⁻'}` : base;
    }
    case 'bnd':
      return 'bornée';
    case 'none':
      return 'pas de limite';
  }
}

function numText(v: number): string {
  if (Number.isInteger(v)) return String(v).replace('-', '−');
  return String(Math.round(v * 1e6) / 1e6)
    .replace('-', '−')
    .replace('.', ',');
}

/** deux limites sont-elles la même ? Un côté écrit doit être le bon ; « 0 » vaut 0⁺ et 0⁻. */
export function sameLimit(claimed: LimVal, truth: LimVal): boolean {
  if (claimed.k === 'inf') return truth.k === 'inf' && truth.s === claimed.s;
  if (claimed.k === 'fin') {
    if (truth.k !== 'fin' || !closeNum(claimed.v, truth.v)) return false;
    if (claimed.side && truth.side && claimed.side !== truth.side) return false;
    // un côté affirmé doit être établi
    return !claimed.side || truth.side === claimed.side;
  }
  return claimed.k === truth.k;
}

/* ------------------------------ opérations ------------------------------ */

interface Arg {
  r: LimRes;
  /** sans la variable : une constante (son côté ne compte pas) */
  c: boolean;
}

function sideOfFinite(args: Arg[], f: (i: number) => Side): Side {
  // le côté d'une somme / d'un produit : un seul terme variable donne son côté
  const vary = args.map((a, i) => ({ a, i })).filter(({ a }) => !a.c);
  if (vary.length !== 1) return 0;
  return f(vary[0]!.i);
}

/** somme (et différence : les termes portent leur signe) */
export function addL(args: Arg[], opts: { bnd?: boolean } = {}): LimRes {
  if (args.some((a) => a.r.k === 'unknown' || a.r.k === 'fi' || a.r.k === 'none')) {
    const f = args.find((a) => a.r.k === 'fi');
    return f ? f.r : UNKNOWN;
  }
  const infs = args.filter((a) => a.r.k === 'inf').map((a) => (a.r as { s: 1 | -1 }).s);
  const bnds = args.filter((a) => a.r.k === 'bnd').length;
  if (infs.includes(1) && infs.includes(-1)) return fi('∞ − ∞');
  if (infs.length) return bnds && !opts.bnd ? UNKNOWN : inf(infs[0]!);
  if (bnds) return opts.bnd ? { k: 'bnd' } : UNKNOWN;
  const v = args.reduce((s, a) => s + (a.r as { v: number }).v, 0);
  const side = sideOfFinite(args, (i) => (args[i]!.r as { side: Side }).side);
  return fin(v, side);
}

/** produit */
export function mulL(args: Arg[], opts: { bnd?: boolean } = {}): LimRes {
  if (args.some((a) => a.r.k === 'unknown' || a.r.k === 'none' || a.r.k === 'fi')) {
    const f = args.find((a) => a.r.k === 'fi');
    return f ? f.r : UNKNOWN;
  }
  const zeros = args.filter((a) => isZero(a.r));
  const infs = args.filter((a) => a.r.k === 'inf');
  const bnds = args.filter((a) => a.r.k === 'bnd');
  if (zeros.length && infs.length) return fi('0 × ∞');
  if (bnds.length) {
    if (!opts.bnd) return UNKNOWN;
    if (zeros.length) return fin(0, 0);
    return infs.length ? UNKNOWN : { k: 'bnd' };
  }
  // signe des facteurs finis non nuls et des infinis
  let s = 1;
  for (const a of args) {
    if (a.r.k === 'inf') s *= a.r.s;
    else if (a.r.k === 'fin' && !isZero(a.r)) s *= sgn(a.r.v);
  }
  if (infs.length) return inf(s > 0 ? 1 : -1);
  const v = args.reduce((p, a) => p * (a.r as { v: number }).v, 1);
  if (zeros.length) {
    // 0 : son côté = côtés des facteurs nuls × signe des autres
    let side = s;
    for (const z of zeros) {
      const zs = (z.r as { side: Side }).side;
      if (!zs) return fin(0, 0);
      side *= zs;
    }
    return fin(0, side > 0 ? 1 : -1);
  }
  const side = sideOfFinite(args, (i) => {
    const r = args[i]!.r as { v: number; side: Side };
    const others = v / r.v;
    return (r.side * sgn(others)) as Side;
  });
  return fin(v, side);
}

/** quotient a ÷ b */
export function divL(a: Arg, b: Arg, opts: { bnd?: boolean } = {}): LimRes {
  if (a.r.k === 'fi') return a.r;
  if (b.r.k === 'fi') return b.r;
  if (a.r.k === 'unknown' || b.r.k === 'unknown' || a.r.k === 'none' || b.r.k === 'none')
    return UNKNOWN;
  if (a.r.k === 'bnd') {
    if (!opts.bnd) return UNKNOWN;
    if (b.r.k === 'inf') return fin(0, 0);
    if (b.r.k === 'fin' && !isZero(b.r)) return { k: 'bnd' };
    return UNKNOWN;
  }
  if (b.r.k === 'bnd') return UNKNOWN;
  if (a.r.k === 'inf' && b.r.k === 'inf') return fi('∞ ÷ ∞');
  if (isZero(a.r) && isZero(b.r)) return fi('0 ÷ 0');
  if (b.r.k === 'inf') {
    if (a.r.k !== 'fin') return UNKNOWN;
    if (isZero(a.r)) return fin(0, 0);
    return fin(0, (sgn(a.r.v) * b.r.s) as Side);
  }
  if (isZero(b.r)) {
    // a non nul (fini ou infini) ÷ 0 : le côté du dénominateur décide
    const sa = a.r.k === 'inf' ? a.r.s : sgn((a.r as { v: number }).v);
    const sb = (b.r as { side: Side }).side;
    if (!sb) return { k: 'none' };
    return inf(sa * sb > 0 ? 1 : -1);
  }
  // b fini non nul
  const vb = (b.r as { v: number }).v;
  if (a.r.k === 'inf') return inf(a.r.s * sgn(vb) > 0 ? 1 : -1);
  const va = a.r.v;
  let side: Side = 0;
  if (b.c) side = (a.r.side * sgn(vb)) as Side;
  else if (a.c) side = (-(b.r as { side: Side }).side * sgn(va)) as Side;
  return fin(va / vb, side);
}

function intExp(e: number): number | null {
  return Number.isInteger(e) ? e : null;
}

/** puissance u^e, e constant (entier ou rationnel), ou q^u (q constant) */
export function powL(base: Arg, expo: Arg, opts: { seq?: boolean; bnd?: boolean } = {}): LimRes {
  if (base.r.k === 'fi') return base.r;
  if (expo.r.k === 'fi') return expo.r;
  if (base.r.k === 'unknown' || expo.r.k === 'unknown') return UNKNOWN;
  if (expo.c && expo.r.k === 'fin') {
    const e = expo.r.v;
    const k = intExp(e);
    if (e === 0) return fin(1, 0);
    if (base.r.k === 'bnd') return opts.bnd && e > 0 ? { k: 'bnd' } : UNKNOWN;
    if (base.r.k === 'none') return UNKNOWN;
    if (base.r.k === 'inf') {
      if (k === null && base.r.s < 0) return UNKNOWN;
      const s = k !== null && k % 2 !== 0 ? base.r.s : 1;
      return e > 0 ? inf(s as 1 | -1) : fin(0, s as Side);
    }
    const v = base.r.v;
    const side = base.r.side;
    if (Math.abs(v) < 1e-12) {
      if (k === null && side < 0) return UNKNOWN;
      const even = k !== null && k % 2 === 0;
      const s: Side = even ? 1 : side;
      if (e > 0) return fin(0, s);
      if (!s) return { k: 'none' };
      return inf(s);
    }
    if (k === null && v < 0) return UNKNOWN;
    const val = v ** e;
    // côté : u ↦ u^e monotone près de v ; croissante si e·v^(e−1) > 0
    const grow = sgn(e * Math.sign(v) ** (k !== null ? k - 1 : 0));
    return fin(val, base.c ? 0 : ((side * grow) as Side));
  }
  if (base.c && base.r.k === 'fin') {
    const q = base.r.v;
    if (expo.r.k === 'inf') {
      if (q > 0) {
        if (closeNum(q, 1)) return fin(1, 0);
        const up = (q > 1 ? 1 : -1) * expo.r.s;
        return up > 0 ? inf(1) : fin(0, 1);
      }
      // q < 0 : seulement pour une suite (n entier)
      if (!opts.seq || expo.r.s < 0) return UNKNOWN;
      if (q > -1) return fin(0, 0);
      return closeNum(q, -1) ? (opts.bnd ? { k: 'bnd' } : UNKNOWN) : { k: 'none' };
    }
    if (expo.r.k === 'fin') {
      if (q <= 0) return UNKNOWN;
      return fin(q ** expo.r.v, 0);
    }
    return UNKNOWN;
  }
  // base et exposant variables : 1^∞, 0⁰, ∞⁰
  if (base.r.k === 'fin' && expo.r.k === 'inf' && closeNum(base.r.v, 1)) return fi('1^∞');
  if (isZero(base.r) && isZero(expo.r)) return fi('0⁰');
  if (base.r.k === 'inf' && isZero(expo.r)) return fi('∞⁰');
  if (base.r.k === 'fin' && base.r.v > 0 && expo.r.k === 'fin') return fin(base.r.v ** expo.r.v, 0);
  if (base.r.k === 'fin' && base.r.v > 0 && expo.r.k === 'inf') {
    const up = (base.r.v > 1 ? 1 : -1) * expo.r.s;
    return up > 0 ? inf(1) : fin(0, 1);
  }
  if (base.r.k === 'inf' && base.r.s > 0 && expo.r.k === 'fin')
    return expo.r.v > 0 ? inf(1) : fin(0, 1);
  if (base.r.k === 'inf' && base.r.s > 0 && expo.r.k === 'inf')
    return expo.r.s > 0 ? inf(1) : fin(0, 1);
  return UNKNOWN;
}

/** fonction usuelle f(u) */
export function fnL(f: string, u: Arg, opts: { bnd?: boolean; v54?: boolean } = {}): LimRes {
  const r = u.r;
  if (r.k === 'fi') return r;
  // v56 : sin, cos, arctan d'une expression sans limite restent bornés (sin(1 ÷ x) en 0)
  if (r.k === 'none' && !opts.v54 && opts.bnd && ['sin', 'cos', 'arctan'].includes(f))
    return { k: 'bnd' };
  if (r.k === 'unknown' || r.k === 'none') return UNKNOWN;
  switch (f) {
    case 'exp':
      if (r.k === 'inf') return r.s > 0 ? inf(1) : fin(0, 1);
      if (r.k === 'bnd') return opts.bnd ? { k: 'bnd' } : UNKNOWN;
      return fin(Math.exp(r.v), u.c ? 0 : r.side);
    case 'ln':
      if (r.k === 'inf') return r.s > 0 ? inf(1) : UNKNOWN;
      if (r.k === 'bnd') return UNKNOWN;
      if (r.v > 1e-12) return fin(Math.log(r.v), u.c ? 0 : r.side);
      if (Math.abs(r.v) < 1e-12 && r.side > 0) return inf(-1);
      return UNKNOWN;
    case 'sqrt':
      if (r.k === 'inf') return r.s > 0 ? inf(1) : UNKNOWN;
      if (r.k === 'bnd') return UNKNOWN;
      if (r.v > 1e-12) return fin(Math.sqrt(r.v), u.c ? 0 : r.side);
      if (Math.abs(r.v) < 1e-12 && r.side >= 0) return fin(0, 1);
      return UNKNOWN;
    case 'abs':
      if (r.k === 'inf') return inf(1);
      if (r.k === 'bnd') return opts.bnd ? { k: 'bnd' } : UNKNOWN;
      return fin(
        Math.abs(r.v),
        Math.abs(r.v) < 1e-12 ? 1 : u.c ? 0 : ((r.side * sgn(r.v)) as Side),
      );
    case 'cos':
    case 'sin':
      if (r.k === 'inf' || r.k === 'bnd') return opts.bnd ? { k: 'bnd' } : UNKNOWN;
      return fin(f === 'cos' ? Math.cos(r.v) : Math.sin(r.v), 0);
    case 'arctan':
      // v56 : en radians ; croissante, tend vers ±π ÷ 2 en ±∞
      if (opts.v54) return UNKNOWN;
      if (r.k === 'inf') return fin((r.s * Math.PI) / 2, -r.s as Side);
      if (r.k === 'bnd') return opts.bnd ? { k: 'bnd' } : UNKNOWN;
      return fin(Math.atan(r.v), u.c ? 0 : r.side);
    default:
      return UNKNOWN;
  }
}

/* ------------------------------ l'expression ------------------------------ */

export const hasVar = (t: Term, x: string): boolean => symbolsOf(t).has(x);

/** la valeur d'une expression sans la variable (NaN : indéfinie) */
export function constValue(t: Term): number {
  try {
    return evalFloat(radians(t), {});
  } catch {
    return NaN;
  }
}

const ARC_FNS = new Set(['arctan', 'arcsin', 'arccos']);
/** une fonction réciproque de trigonométrie dans l'expression */
export function hasArc(t: Term): boolean {
  switch (t.k) {
    case 'num':
    case 'sym':
      return false;
    case 'add':
    case 'mul':
      return t.c.some(hasArc);
    case 'fn':
      return ARC_FNS.has(t.f) || hasArc(t.a);
    default:
      return hasArc(t.a) || hasArc(t.b);
  }
}
const DEG_TO_RAD: Term = { k: 'div', a: { k: 'sym', n: 'π' }, b: num(180) };
/**
 * Les limites parlent en RADIANS ; le calcul du noyau donne arctan, arcsin, arccos en
 * degrés (convention des triangles) : on convertit pour évaluer.
 */
function radians(t: Term): Term {
  // déjà en radians (moteur v60, `withRadianArcs`) : rien à convertir
  if (!hasArc(t) || radianArcs()) return t;
  switch (t.k) {
    case 'num':
    case 'sym':
      return t;
    case 'add':
    case 'mul':
      return { ...t, c: t.c.map(radians) };
    case 'fn': {
      const a = radians(t.a);
      return ARC_FNS.has(t.f) ? { k: 'mul', c: [DEG_TO_RAD, { ...t, a }] } : { ...t, a };
    }
    default:
      return { ...t, a: radians(t.a), b: radians(t.b) } as Term;
  }
}

/** la variable au point */
export function pointLimit(P: LimPoint): LimVal {
  return P.inf ? inf(P.inf) : fin(P.a, P.side);
}

/**
 * Limites de RÉFÉRENCE au point (sans calcul) : x, xⁿ, 1 ÷ xⁿ, √x, 1 ÷ √x, eˣ, e⁻ˣ,
 * ln(x), qⁿ (suite), et en un réel : x, et 1 ÷ x, 1 ÷ xⁿ, ln(x), √x en 0.
 */
export function referenceLimit(t: Term, P: LimPoint): LimVal | null {
  const x = P.x;
  const isX = (u: Term) => u.k === 'sym' && u.n === x;
  const negX = (u: Term) =>
    u.k === 'mul' &&
    u.c.length === 2 &&
    u.c.some((c) => c.k === 'num' && Q.eq(c.v, Q.rat(-1))) &&
    u.c.some(isX);
  const powX = (u: Term): number | null =>
    isX(u)
      ? 1
      : u.k === 'pow' && isX(u.a) && u.b.k === 'num' && Q.sign(u.b.v) > 0
        ? Q.ratToNumber(u.b.v)
        : u.k === 'fn' && u.f === 'sqrt' && isX(u.a)
          ? 0.5
          : null;
  const recip = (u: Term): number | null =>
    u.k === 'div' && u.a.k === 'num' && Q.eq(u.a.v, Q.ONE) ? powX(u.b) : null;
  const r = (lim: LimRes): LimVal | null => (lim.k === 'unknown' || lim.k === 'fi' ? null : lim);
  const pX: Arg = { r: pointLimit(P), c: false };
  const p = powX(t);
  if (p !== null) return r(powL(pX, { r: fin(p), c: true }));
  const q = recip(t);
  if (q !== null)
    return r(divL({ r: fin(1), c: true }, { r: powL(pX, { r: fin(q), c: true }), c: false }));
  if (t.k === 'fn' && t.f === 'exp' && (isX(t.a) || negX(t.a)))
    return r(
      fnL('exp', { r: isX(t.a) ? pointLimit(P) : mulL([{ r: fin(-1), c: true }, pX]), c: false }),
    );
  if (t.k === 'fn' && t.f === 'ln' && isX(t.a)) return r(fnL('ln', pX));
  // v56 : arctan(x) en ±∞
  if (t.k === 'fn' && t.f === 'arctan' && isX(t.a) && P.inf && !P.v54) return r(fnL('arctan', pX));
  // qⁿ (suite), qˣ (fonction en ±∞)
  if (t.k === 'pow' && !hasVar(t.a, x) && isX(t.b) && P.inf) {
    const q = constValue(t.a);
    if (Number.isFinite(q)) return r(powL({ r: fin(q), c: true }, pX, { seq: P.seq }));
  }
  return null;
}

/**
 * Continuité en un réel a : l'expression est définie et continue en a (les dénominateurs
 * ne s'annulent pas, ln et √ d'un argument strictement positif) → sa valeur.
 */
export function continuousAt(t: Term, P: LimPoint): number | null {
  if (P.inf) return null;
  const env = { [P.x]: P.a };
  const ok = (u: Term): boolean => {
    switch (u.k) {
      case 'num':
      case 'sym':
        return true;
      case 'add':
      case 'mul':
        return u.c.every(ok);
      case 'div': {
        if (!ok(u.a) || !ok(u.b)) return false;
        const d = safeEval(u.b, env);
        return Number.isFinite(d) && Math.abs(d) > 1e-12;
      }
      case 'pow': {
        if (!ok(u.a) || !ok(u.b)) return false;
        const b = safeEval(u.a, env);
        const e = safeEval(u.b, env);
        if (!Number.isFinite(b) || !Number.isFinite(e)) return false;
        if (Math.abs(b) < 1e-12) return e > 0 && Number.isInteger(e);
        return b > 0 || Number.isInteger(e);
      }
      case 'fn': {
        if (!ok(u.a)) return false;
        const v = safeEval(u.a, env);
        if (!Number.isFinite(v)) return false;
        if (u.f === 'ln' || u.f === 'log') return v > 1e-12;
        if (u.f === 'sqrt') return v > 1e-12;
        if (u.f === 'arctan') return !P.v54;
        return ['exp', 'cos', 'sin', 'abs'].includes(u.f);
      }
      default:
        return false;
    }
  };
  if (!ok(t)) return null;
  const v = safeEval(t, env);
  return Number.isFinite(v) ? v : null;
}

/** la fraction rationnelle d'une expression (null : impossible) */
export function toRatFnSafe(t: Term): ReturnType<typeof toRatFn> | null {
  try {
    return toRatFn(t);
  } catch {
    return null;
  }
}

/** un réel du point (« 2 », « −1 », « 0,5 ») en rationnel exact */
function ratOf(v: number): Q.Rat | null {
  const r = Q.parseDecimal(String(Math.abs(v)).replace('.', ','));
  return r ? (v < 0 ? Q.neg(r) : r) : null;
}

function safeEval(t: Term, env: Record<string, number>): number {
  try {
    return evalFloat(radians(t), env);
  } catch {
    return NaN;
  }
}

/* ------------------------------ croissances comparées ------------------------------ */

/** c · x^a · e^(b·x) · (ln x)^l */
export interface Gauge {
  c: number;
  a: number;
  b: number;
  l: number;
}
const G1: Gauge = { c: 1, a: 0, b: 0, l: 0 };
const gMul = (g: Gauge, h: Gauge): Gauge => ({
  c: g.c * h.c,
  a: g.a + h.a,
  b: g.b + h.b,
  l: g.l + h.l,
});
function gPow(g: Gauge, k: number): Gauge | null {
  if (g.c < 0 && !Number.isInteger(k)) return null;
  return { c: g.c ** k, a: g.a * k, b: g.b * k, l: g.l * k };
}

/** u = α·x + β (exactement) */
function linearIn(u: Term, x: string): { al: number; be: number } | null {
  if (!hasVar(u, x)) {
    const v = constValue(u);
    return Number.isFinite(v) ? { al: 0, be: v } : null;
  }
  try {
    const rf = toRatFn(u);
    const d = polyConst(rf.d);
    if (d === null) return null;
    const co = univariate(rf.n, x);
    if (!co || [...co.keys()].some((k) => k > 1)) return null;
    return {
      al: Q.ratToNumber(co.get(1) ?? Q.ZERO) / d,
      be: Q.ratToNumber(co.get(0) ?? Q.ZERO) / d,
    };
  } catch {
    return null;
  }
}

function polyConst(p: Poly): number | null {
  if ([...p.t.values()].some((t) => t.m.length)) return null;
  const c = p.t.get('');
  return c ? Q.ratToNumber(c.c) : null;
}

/** coefficients d'un polynôme en x seul (null : autres lettres, atomes) */
export function univariate(p: Poly, x: string): Map<number, Q.Rat> | null {
  const out = new Map<number, Q.Rat>();
  for (const { m, c } of p.t.values()) {
    if (m.length > 1 || (m.length === 1 && m[0]!.a.symbol !== x)) return null;
    const e = m.length ? m[0]!.e : 0;
    out.set(e, Q.add(out.get(e) ?? Q.ZERO, c));
  }
  return out;
}

/** un produit de puissances de x, d'exponentielles de x et de ln(x) */
export function gaugeOf(t: Term, x: string): Gauge | null {
  switch (t.k) {
    case 'num': {
      const v = Q.ratToNumber(t.v);
      return v ? { ...G1, c: v } : null;
    }
    case 'sym': {
      if (t.n === x) return { ...G1, a: 1 };
      const v = constValue(t);
      return Number.isFinite(v) && v ? { ...G1, c: v } : null;
    }
    case 'mul': {
      let g: Gauge | null = G1;
      for (const c of t.c) {
        const h = gaugeOf(c, x);
        if (!h) return null;
        g = gMul(g, h);
      }
      return g;
    }
    case 'div': {
      const g = gaugeOf(t.a, x);
      const h = gaugeOf(t.b, x);
      if (!g || !h) return null;
      const hi = gPow(h, -1);
      return hi ? gMul(g, hi) : null;
    }
    case 'pow': {
      if (!hasVar(t.b, x)) {
        const k = constValue(t.b);
        const g = gaugeOf(t.a, x);
        return g && Number.isFinite(k) ? gPow(g, k) : null;
      }
      if (hasVar(t.a, x)) return null;
      const q = constValue(t.a);
      const lin = linearIn(t.b, x);
      if (!(q > 0) || !lin) return null;
      return { ...G1, c: q ** lin.be, b: lin.al * Math.log(q) };
    }
    case 'fn': {
      if (t.f === 'exp') {
        const lin = linearIn(t.a, x);
        return lin ? { ...G1, c: Math.exp(lin.be), b: lin.al } : null;
      }
      if (t.f === 'ln') {
        if (t.a.k === 'sym' && t.a.n === x) return { ...G1, l: 1 };
        return null;
      }
      if (t.f === 'sqrt') {
        const g = gaugeOf(t.a, x);
        return g ? gPow(g, 0.5) : null;
      }
      return null;
    }
    default:
      return null;
  }
}

/** limite d'une jauge en +∞ (`at` = 1) ou en 0⁺ (`at` = 0) */
export function gaugeLimit(g: Gauge, at: 1 | 0): LimVal | null {
  const eps = 1e-12;
  if (at === 1) {
    const s = sgn(g.c) as 1 | -1;
    if (Math.abs(g.b) > eps) return g.b > 0 ? inf(s) : fin(0, s);
    if (Math.abs(g.a) > eps) return g.a > 0 ? inf(s) : fin(0, s);
    if (Math.abs(g.l) > eps) return g.l > 0 ? inf(s) : fin(0, s);
    return fin(g.c, 0);
  }
  // en 0⁺ : ln x < 0, e^(bx) → 1
  if (!Number.isInteger(g.l)) return null;
  const s = (sgn(g.c) * (g.l % 2 !== 0 ? -1 : 1)) as 1 | -1;
  if (Math.abs(g.a) > eps) return g.a > 0 ? fin(0, s) : inf(s);
  if (Math.abs(g.l) > eps) return g.l > 0 ? inf(s) : fin(0, s);
  return fin(g.c, 0);
}

/** les tendances des facteurs s'opposent : une forme indéterminée levée par le cours */
export function gaugeConflict(g: Gauge, at: 1 | 0): boolean {
  const eps = 1e-12;
  const tend: number[] = [];
  if (at === 1) {
    if (Math.abs(g.b) > eps) tend.push(Math.sign(g.b));
    if (Math.abs(g.a) > eps) tend.push(Math.sign(g.a));
    if (Math.abs(g.l) > eps) tend.push(Math.sign(g.l));
  } else {
    if (Math.abs(g.a) > eps) tend.push(-Math.sign(g.a));
    if (Math.abs(g.l) > eps) tend.push(Math.sign(g.l));
  }
  return tend.includes(1) && tend.includes(-1);
}

/**
 * Le terme mis en jauge au point : en +∞ directement, en −∞ par x ↦ −x, en a⁺ / a⁻ par
 * x ↦ a ± x (vers 0⁺). null : pas une jauge, ou un point à deux côtés.
 */
export function gaugeAt(t: Term, P: LimPoint): { g: Gauge; at: 1 | 0 } | null {
  const tt = moveToCanonical(t, P);
  if (!tt) return null;
  const g = gaugeOf(tt.t, P.x);
  return g ? { g, at: tt.at } : null;
}

function moveToCanonical(t: Term, P: LimPoint): { t: Term; at: 1 | 0 } | null {
  const x = sym(P.x);
  if (P.inf === 1) return { t, at: 1 };
  if (P.inf === -1) return { t: substitute(t, { [P.x]: { k: 'mul', c: [num(-1), x] } }), at: 1 };
  if (!P.side) return null;
  if (P.a === 0 && P.side > 0) return { t, at: 0 };
  const a = ratOf(P.a);
  if (!a) return null;
  const shifted: Term =
    P.side > 0
      ? { k: 'add', c: [num(a), x] }
      : { k: 'add', c: [num(a), { k: 'mul', c: [num(-1), x] }] };
  return {
    t: substitute(t, { [P.x]: P.a === 0 ? { k: 'mul', c: [num(-1), x] } : shifted }),
    at: 0,
  };
}

/* ------------------------------ nombre dérivé ------------------------------ */

/**
 * (f(x) − f(a)) ÷ (x − a) en a : la limite est f′(a). Le dénominateur s'écrit « x − a »
 * (« x » en 0) ; le numérateur s'annule en a.
 */
export function derivativeLimit(t: Term, P: LimPoint): number | null {
  if (P.inf || t.k !== 'div') return null;
  const x = P.x;
  const den = t.b;
  const isDen =
    linearIn(den, x) && closeNum(linearIn(den, x)!.al, 1) && closeNum(linearIn(den, x)!.be, -P.a);
  if (!isDen) return null;
  const f = t.a;
  // un numérateur polynôme se factorise (on simplifie la fraction) : pas de nombre dérivé
  const rf = toRatFnSafe(f);
  if (rf && univariate(rf.n, x) && polyConst(rf.d) !== null) return null;
  const fa = safeEval(f, { [x]: P.a });
  if (!Number.isFinite(fa) || Math.abs(fa) > 1e-9) return null;
  if (continuousAt(f, P) === null) return null;
  let d: Term;
  try {
    d = derive(f, x);
  } catch {
    // v56 : une fonction que le calcul ne sait pas dériver (arctan) — dérivée numérique
    // centrée, gardée seulement si elle tombe sur une valeur « remarquable »
    return P.v54 ? null : numericDerivative(f, x, P.a);
  }
  const v = safeEval(d, { [x]: P.a });
  return Number.isFinite(v) ? v : null;
}

function numericDerivative(f: Term, x: string, a: number): number | null {
  const est = (h: number) => (safeEval(f, { [x]: a + h }) - safeEval(f, { [x]: a - h })) / (2 * h);
  const d1 = est(1e-4);
  const d2 = est(1e-5);
  if (!Number.isFinite(d1) || !Number.isFinite(d2)) return null;
  if (Math.abs(d1 - d2) > 1e-6 * Math.max(1, Math.abs(d2))) return null;
  const near = NICE.filter((v) => Math.abs(v - d2) <= 1e-7 * Math.max(1, Math.abs(d2)));
  return near.length === 1 ? near[0]! : null;
}

/* ------------------------------ ce qu'on conclut ------------------------------ */

export type Via =
  | 'const'
  | 'reference'
  | 'continuity'
  | 'op'
  | 'compare'
  | 'derivative'
  | 'established';

export interface KnownOpts {
  /** limites déjà établies (feuille) */
  lookup?: (t: Term) => LimVal | null;
  /** les opérations avec une suite bornée (théorèmes de comparaison) */
  bnd?: boolean;
  /** sans les deux théorèmes (croissances comparées, nombre dérivé) */
  noTheorems?: boolean;
}

/** ce qu'un élève de Terminale conclut de l'écriture (formes indéterminées non levées) */
export function knownLimit(t: Term, P: LimPoint, opts: KnownOpts = {}): LimRes {
  const got = opts.lookup?.(t);
  if (got) return got;
  if (!hasVar(t, P.x)) {
    const v = constValue(t);
    return Number.isFinite(v) ? fin(v, 0) : UNKNOWN;
  }
  if (t.k === 'sym') return pointLimit(P);
  const ref = referenceLimit(t, P);
  if (ref) return ref;
  const arg = (u: Term): Arg => ({ r: knownLimit(u, P, opts), c: !hasVar(u, P.x) });
  let r: LimRes;
  switch (t.k) {
    case 'add':
      r = addL(t.c.map(arg), opts);
      break;
    case 'mul':
      r = mulL(t.c.map(arg), opts);
      break;
    case 'div':
      r = divL(arg(t.a), arg(t.b), opts);
      break;
    case 'pow':
      r = powL(arg(t.a), arg(t.b), { seq: P.seq, bnd: opts.bnd });
      break;
    case 'fn':
      r = fnL(t.f, arg(t.a), { ...opts, v54: P.v54 });
      break;
    default:
      r = UNKNOWN;
  }
  if (isDet(r) || r.k === 'bnd' || r.k === 'none') return r;
  // les deux théorèmes du cours
  if (!opts.noTheorems) {
    const th = theoremLimit(t, P);
    if (th) return th.v;
  }
  // continuité en un réel
  const c = continuousAt(t, P);
  if (c !== null) return fin(c, 0);
  return r;
}

/** croissances comparées, nombre dérivé ; v56 : les mêmes sur une composée */
export function theoremLimit(
  t: Term,
  P: LimPoint,
): { v: LimVal; via: 'compare' | 'derivative'; composite?: Term } | null {
  const ga = gaugeAt(t, P);
  if (ga && gaugeConflict(ga.g, ga.at)) {
    const v = gaugeLimit(ga.g, ga.at);
    if (v) return { v, via: 'compare' };
  }
  const d = derivativeLimit(t, P);
  if (d !== null) return { v: fin(d, 0), via: 'derivative' };
  if (!P.v54) {
    const c = compositeLimit(t, P);
    if (c) return c;
  }
  return null;
}

/** la variable de la composée (jamais une lettre d'une feuille) */
const COMPOSE_VAR = '𝑋';

const keyOfTerm = (t: Term): string => {
  try {
    return termKey(canon(t));
  } catch {
    return termKey(t);
  }
};

/** les sous-écritures qui dépendent de la variable (sans la variable seule ni t) */
function innerParts(t: Term, x: string): Term[] {
  const out: Term[] = [];
  const walk = (u: Term, top: boolean) => {
    if (!top && u.k !== 'sym' && u.k !== 'num' && hasVar(u, x)) out.push(u);
    switch (u.k) {
      case 'add':
      case 'mul':
        u.c.forEach((c) => walk(c, false));
        break;
      case 'fn':
        walk(u.a, false);
        break;
      case 'div':
      case 'pow':
      case 'binom':
        walk(u.a, false);
        walk(u.b, false);
        break;
      default:
        break;
    }
  };
  walk(t, true);
  return out;
}

/** t où chaque occurrence de U (même écriture) est remplacée par X */
function replacePart(t: Term, key: string, X: Term): Term {
  if (t.k !== 'num' && t.k !== 'sym' && keyOfTerm(t) === key) return X;
  switch (t.k) {
    case 'add':
    case 'mul':
      return { ...t, c: t.c.map((c) => replacePart(c, key, X)) };
    case 'fn':
      return { ...t, a: replacePart(t.a, key, X) };
    case 'div':
    case 'pow':
    case 'binom':
      return { ...t, a: replacePart(t.a, key, X), b: replacePart(t.b, key, X) } as Term;
    default:
      return t;
  }
}

/**
 * Une COMPOSÉE (v56) : t = g(U), U a une limite, et g a une limite au point par un des deux
 * théorèmes (sin(X) ÷ X, (eˣ − 1) ÷ X, ln(1 + X) ÷ X, ln(X) ÷ X, e^X ÷ X…). Toute la
 * variable doit disparaître quand U devient X : « n × sin(1 ÷ n) » n'en est pas une, il faut
 * d'abord l'écrire « sin(1 ÷ n) ÷ (1 ÷ n) ».
 */
export function compositeLimit(
  t: Term,
  P: LimPoint,
): { v: LimVal; via: 'compare' | 'derivative'; composite: Term } | null {
  const x = P.x;
  const X: Term = { k: 'sym', n: COMPOSE_VAR };
  const seen = new Set<string>();
  for (const U of innerParts(t, x)) {
    const key = keyOfTerm(U);
    if (seen.has(key)) continue;
    seen.add(key);
    const g = replacePart(t, key, X);
    if (hasVar(g, x) || !hasVar(g, COMPOSE_VAR)) continue;
    const lu = knownLimit(U, P);
    if (lu.k !== 'fin' && lu.k !== 'inf') continue;
    const Q: LimPoint =
      lu.k === 'inf'
        ? { x: COMPOSE_VAR, inf: lu.s, a: 0, side: 0, seq: false }
        : { x: COMPOSE_VAR, inf: 0, a: lu.v, side: lu.side, seq: false };
    const th = theoremLimit(g, Q);
    if (th) return { v: th.v, via: th.via, composite: U };
  }
  return null;
}

/* ------------------------------ la vraie limite ------------------------------ */

/** limite exacte d'une fraction rationnelle en x (null : autre chose) */
export function rationalLimit(t: Term, P: LimPoint): LimVal | null {
  let rf: ReturnType<typeof toRatFn>;
  try {
    rf = toRatFn(t);
  } catch {
    return null;
  }
  const N = univariate(rf.n, P.x);
  const D = univariate(rf.d, P.x);
  if (!N || !D) return null;
  const deg = (m: Map<number, Q.Rat>) =>
    Math.max(-1, ...[...m.entries()].filter(([, c]) => !Q.isZero(c)).map(([k]) => k));
  if (deg(D) < 0) return null;
  if (deg(N) < 0) return fin(0, 0);
  if (P.inf) {
    const dn = deg(N);
    const dd = deg(D);
    const lc = Q.ratToNumber(N.get(dn)!) / Q.ratToNumber(D.get(dd)!);
    const xs = P.inf;
    if (dn === dd) return fin(lc, 0);
    if (dn < dd) return fin(0, (sgn(lc) * xs ** (dd - dn)) as Side);
    return inf((sgn(lc) * xs ** (dn - dd)) as 1 | -1);
  }
  // en a : développement de Taylor exact, ordre d'annulation
  const a = ratOf(P.a);
  if (!a) return null;
  const shift = (m: Map<number, Q.Rat>): Q.Rat[] => {
    // coefficients de P(a + t) par la formule du binôme
    const n = deg(m);
    const out: Q.Rat[] = Array.from({ length: n + 1 }, () => Q.ZERO);
    for (const [k, c] of m) {
      let binom = 1n;
      for (let j = 0; j <= k; j++) {
        // c · C(k, j) · a^(k − j) · t^j
        const term = Q.mul(Q.mul(c, Q.rat(binom)), Q.pow(a, k - j)!);
        out[j] = Q.add(out[j]!, term);
        binom = (binom * BigInt(k - j)) / BigInt(j + 1);
      }
    }
    return out;
  };
  const sn = shift(N);
  const sd = shift(D);
  const ord = (s: Q.Rat[]) => s.findIndex((c) => !Q.isZero(c));
  const on = ord(sn);
  const od = ord(sd);
  if (od < 0) return null;
  if (on < 0) return fin(0, 0);
  const lc = Q.ratToNumber(sn[on]!) / Q.ratToNumber(sd[od]!);
  const k = on - od;
  if (k === 0) return fin(lc, 0);
  const sideOf = (s: Side): number => (s > 0 ? 1 : (-1) ** k);
  if (!P.side) {
    // deux côtés : il faut le même résultat
    if (k > 0) return fin(0, k % 2 === 0 ? (sgn(lc) as Side) : 0);
    if (-k % 2 === 0) return inf(sgn(lc) as 1 | -1);
    return { k: 'none' };
  }
  const s = sgn(lc) * sideOf(P.side);
  if (k > 0) return fin(0, s as Side);
  return inf(s as 1 | -1);
}

/** terme dominant (sommes de jauges), avec l'exactitude de l'équivalent */
interface Asym {
  g: Gauge;
  exact: boolean;
}

function dominates(g: Gauge, h: Gauge, at: 1 | 0): number {
  const c = (u: number, v: number) => (Math.abs(u - v) < 1e-12 ? 0 : u > v ? 1 : -1);
  if (at === 1) return c(g.b, h.b) || c(g.a, h.a) || c(g.l, h.l);
  return c(h.a, g.a) || c(g.l, h.l);
}

function asymOf(t: Term, x: string, at: 1 | 0): Asym | null {
  const g = gaugeOf(t, x);
  if (g) return { g, exact: at === 1 || Math.abs(g.b) < 1e-12 };
  switch (t.k) {
    case 'add': {
      const parts = t.c.map((c) => asymOf(c, x, at));
      if (parts.some((p) => !p)) return null;
      const groups: { g: Gauge; exact: boolean; scale: number }[] = [];
      for (const p of parts as Asym[]) {
        const same = groups.find(
          (q) => dominates(q.g, p.g, at) === 0 && (at === 0 || Math.abs(q.g.b - p.g.b) < 1e-12),
        );
        if (same) {
          same.g = { ...same.g, c: same.g.c + p.g.c };
          same.exact &&= p.exact;
          same.scale = Math.max(same.scale, Math.abs(p.g.c));
        } else groups.push({ g: { ...p.g }, exact: p.exact, scale: Math.abs(p.g.c) });
      }
      groups.sort((p, q) => -dominates(p.g, q.g, at));
      for (const q of groups) {
        if (Math.abs(q.g.c) <= 1e-12 * Math.max(1, q.scale)) {
          if (!q.exact) return null;
          continue;
        }
        return { g: q.g, exact: q.exact && groups.length === 1 };
      }
      return null;
    }
    case 'mul': {
      let g: Gauge = G1;
      let exact = true;
      for (const c of t.c) {
        const a = asymOf(c, x, at);
        if (!a) return null;
        g = gMul(g, a.g);
        exact &&= a.exact;
      }
      return { g, exact };
    }
    case 'div': {
      const a = asymOf(t.a, x, at);
      const b = asymOf(t.b, x, at);
      if (!a || !b) return null;
      const bi = gPow(b.g, -1);
      return bi ? { g: gMul(a.g, bi), exact: a.exact && b.exact } : null;
    }
    case 'pow': {
      if (hasVar(t.b, x)) return null;
      const k = constValue(t.b);
      const a = asymOf(t.a, x, at);
      if (!a || !Number.isFinite(k)) return null;
      const g = gPow(a.g, k);
      return g ? { g, exact: a.exact } : null;
    }
    case 'fn': {
      if (t.f === 'sqrt') {
        const a = asymOf(t.a, x, at);
        const g = a && gPow(a.g, 0.5);
        return a && g ? { g, exact: a.exact } : null;
      }
      if (t.f === 'ln' && at === 1) {
        const a = asymOf(t.a, x, at);
        if (!a || a.g.c <= 0) return null;
        if (Math.abs(a.g.b) > 1e-12) return { g: { ...G1, c: a.g.b, a: 1 }, exact: false };
        if (Math.abs(a.g.a) > 1e-12) return { g: { ...G1, c: a.g.a, l: 1 }, exact: false };
        if (Math.abs(a.g.l) > 1e-12) return null;
        return { g: { ...G1, c: Math.log(a.g.c) }, exact: false };
      }
      return null;
    }
    default:
      return null;
  }
}

/** limite par le terme dominant (null : on ne sait pas) */
export function asymLimit(t: Term, P: LimPoint): LimVal | null {
  if (!P.inf && !P.side) {
    const r = asymLimit(t, { ...P, side: 1 });
    const l = asymLimit(t, { ...P, side: -1 });
    if (!r || !l) return null;
    if (r.k === 'inf' && l.k === 'inf') return r.s === l.s ? r : { k: 'none' };
    if (r.k === 'fin' && l.k === 'fin' && closeNum(r.v, l.v))
      return fin(r.v, r.side === l.side ? r.side : 0);
    return { k: 'none' };
  }
  const tt = moveToCanonical(t, P);
  if (!tt) return null;
  const a = asymOf(canonSafe(tt.t), P.x, tt.at);
  if (!a) return null;
  const v = gaugeLimit(a.g, tt.at);
  // une limite finie non nulle d'un équivalent : le côté reste inconnu
  if (v && v.k === 'fin' && !a.exact && Math.abs(v.v) > 1e-12) return fin(v.v, 0);
  return v;
}

/* ------------------------------ numérique ------------------------------ */

function approachPoints(P: LimPoint, dense = false): number[] {
  if (P.inf) {
    const base = P.seq
      ? dense
        ? [7, 12, 25, 50, 101, 250, 1000, 4001]
        : [10, 100, 1000, 10000, 100000, 1000000]
      : dense
        ? [11.3, 23.7, 47.1, 97.9, 211.3, 503.7, 1013.9]
        : [10, 100, 1000, 10000, 100000, 1e6, 1e7, 1e8];
    return base.map((v) => v * P.inf);
  }
  const hs = dense
    ? [0.31, 0.13, 0.071, 0.023, 0.0089, 0.0031, 0.0007]
    : [1e-1, 1e-2, 1e-3, 1e-4, 1e-5, 1e-6, 1e-7, 1e-8];
  if (P.side) return hs.map((h) => P.a + P.side * h);
  return hs.flatMap((h) => [P.a + h, P.a - h]);
}

/** valeurs d'une expression près du point (NaN : indéfinie) */
export function valuesNear(t: Term, P: LimPoint, dense = true): { p: number; v: number }[] {
  return approachPoints(P, dense).map((p) => ({ p, v: safeEval(t, { [P.x]: p }) }));
}

/**
 * Évaluation numérique PRUDENTE : 'fin' (valeur approchée), 'inf', ou null quand les
 * valeurs ne se décident pas. Sert seulement à confirmer ou réfuter.
 */
export function numericLimit(t: Term, P: LimPoint): LimVal | null {
  if (!P.inf && !P.side) {
    const r = numericLimit(t, { ...P, side: 1 });
    const l = numericLimit(t, { ...P, side: -1 });
    if (!r || !l) return null;
    if (r.k === 'inf' && l.k === 'inf') return r.s === l.s ? r : { k: 'none' };
    if (r.k === 'fin' && l.k === 'fin' && closeLoose(r.v, l.v))
      return fin((r.v + l.v) / 2, r.side === l.side ? r.side : 0);
    return { k: 'none' };
  }
  const vs = valuesNear(t, P, false).map((s) => s.v);
  // une suite : n et n + 1 (oscillation)
  if (P.seq) {
    const alt = approachPoints(P, false).map((p) => safeEval(t, { [P.x]: p + 1 }));
    const last = vs[vs.length - 1]!;
    const lastAlt = alt[alt.length - 1]!;
    if (Number.isFinite(last) && Number.isFinite(lastAlt) && !closeLoose(last, lastAlt)) {
      if (Math.abs(last) > 1e6 && Math.abs(lastAlt) > 1e6 && Math.sign(last) !== Math.sign(lastAlt))
        return { k: 'none' };
    }
  }
  const tail = vs.filter((v) => !Number.isNaN(v)).slice(-4);
  if (tail.length < 4) return null;
  if (tail.every((v) => v === Infinity)) return inf(1);
  if (tail.every((v) => v === -Infinity)) return inf(-1);
  const fins = tail.map((v) => (Number.isFinite(v) ? v : Math.sign(v) * 1e308));
  const mags = fins.map(Math.abs);
  const sameSign = fins.every((v) => Math.sign(v) === Math.sign(fins[3]!));
  if (sameSign && mags[3]! > 1e6 && mags.every((m, i) => i === 0 || m >= mags[i - 1]! * 1.5))
    return inf(fins[3]! > 0 ? 1 : -1);
  // convergence : des écarts qui décroissent géométriquement, la limite extrapolée
  const d = fins.slice(1).map((v, i) => v - fins[i]!);
  const ad = d.map(Math.abs);
  const scale = Math.max(1, Math.abs(fins[3]!));
  if (ad[2]! <= 1e-12 * scale) return snapped(fins[3]!, 1e-9 * scale, fins);
  const r1 = ad[1]! / Math.max(ad[0]!, 1e-300);
  const r2 = ad[2]! / Math.max(ad[1]!, 1e-300);
  const sameDir = Math.sign(d[1]!) === Math.sign(d[2]!) && Math.sign(d[0]!) === Math.sign(d[1]!);
  if (sameDir && r1 < 0.7 && r2 < 0.7 && Math.abs(r1 - r2) < 0.35) {
    const r = Math.max(r1, r2);
    const rest = (ad[2]! * r) / (1 - r);
    const est = fins[3]! + Math.sign(d[2]!) * rest;
    return snapped(est, Math.max(rest, 1e-9 * scale) * 2, fins);
  }
  return null;
}

/** les valeurs « remarquables » proches d'une estimation (un seul candidat, sinon null) */
const NICE: number[] = (() => {
  const out: number[] = [];
  for (let n = -60; n <= 60; n++) for (const d of [1, 2, 3, 4, 5, 6, 8, 10]) out.push(n / d);
  for (const c of [
    Math.E,
    1 / Math.E,
    Math.sqrt(Math.E),
    Math.exp(-0.5),
    Math.LN2,
    Math.PI,
    Math.SQRT2,
  ])
    out.push(c, -c, 2 * c);
  return [...new Set(out.map((v) => Math.round(v * 1e12) / 1e12))];
})();

function snapped(est: number, tol: number, fins: number[]): LimVal | null {
  const near = NICE.filter((v) => Math.abs(v - est) <= Math.max(tol, 1e-9));
  if (near.length !== 1) return null;
  const v = near[0]!;
  if (Math.abs(v) < 1e-12)
    return fin(0, fins.every((u) => u > 0) ? 1 : fins.every((u) => u < 0) ? -1 : 0);
  return fin(v, 0);
}

function closeLoose(a: number, b: number): boolean {
  return Math.abs(a - b) <= 1e-6 * Math.max(1, Math.abs(a), Math.abs(b));
}

/** le côté d'une limite nulle, d'après le signe des valeurs près du point */
export function numericSide(t: Term, P: LimPoint): Side {
  // beaucoup de points : une suite qui change de signe (cos(n) ÷ n) ne passe pas
  const pts = P.inf
    ? P.seq
      ? Array.from({ length: 120 }, (_, i) => 10 + i * 7 + (i % 3))
      : Array.from({ length: 120 }, (_, i) => P.inf * (5 + i * 3.7 + (i * i) / 9))
    : Array.from({ length: 60 }, (_, i) => 0.3 / (1 + i * 0.7)).flatMap((h) =>
        P.side ? [P.a + P.side * h] : [P.a + h, P.a - h],
      );
  const vs = pts.map((p) => safeEval(t, { [P.x]: p })).filter((v) => Number.isFinite(v));
  if (vs.length < 4) return 0;
  if (vs.every((v) => v > 0)) return 1;
  if (vs.every((v) => v < 0)) return -1;
  return 0;
}

export type Provenance = 'known' | 'rational' | 'asym' | 'numeric' | 'series';

function canonSafe(t: Term): Term {
  try {
    return canon(t);
  } catch {
    return t;
  }
}

/** p + q, avec une racine : (p² − q²) ÷ (p − q), les carrés de racines simplifiés */
export function conjugateOf(t: Term): Term | null {
  if (t.k !== 'add' || t.c.length !== 2) return null;
  const hasRoot = (u: Term): boolean =>
    (u.k === 'fn' && u.f === 'sqrt') || (u.k === 'mul' && u.c.some(hasRoot));
  const [p, q] = t.c as [Term, Term];
  if (!hasRoot(p) && !hasRoot(q)) return null;
  const sq = (u: Term): Term => {
    if (u.k === 'fn' && u.f === 'sqrt') return u.a;
    if (u.k === 'mul')
      return {
        k: 'mul',
        c: u.c.map((c) => (c.k === 'num' ? { k: 'num', v: Q.mul(c.v, c.v) } : sq(c))),
      };
    return { k: 'pow', a: u, b: num(2) };
  };
  const neg = (u: Term): Term => ({ k: 'mul', c: [num(-1), u] });
  const top: Term = { k: 'add', c: [sq(p), neg(sq(q))] };
  let a: Term = top;
  try {
    a = rfToTerm(toRatFn(top));
  } catch {
    /* gardée telle quelle */
  }
  return { k: 'div', a, b: { k: 'add', c: [p, neg(q)] } };
}

/** la vraie limite, quand on sait la trouver */
export function trueLimit(
  t: Term,
  P: LimPoint,
  opts: KnownOpts = {},
): { v: LimVal | null; via: Provenance | null } {
  const withSide = (v: LimVal): LimVal =>
    v.k === 'fin' && Math.abs(v.v) < 1e-12 && !v.side ? fin(0, numericSide(t, P)) : v;
  const k = knownLimit(t, P, { ...opts, bnd: true });
  if (isDet(k) || k.k === 'none') return { v: withSide(k as LimVal), via: 'known' };
  // v58 : en 0, par les développements limités (exact)
  if (P.dl && !P.inf && Math.abs(P.a) < 1e-12) {
    const L = seriesLimitOf(t, P.x, P.side);
    if (L) return { v: withSide(L.k === 'fin' ? fin(L.v) : inf(L.s)), via: 'series' };
  }
  // une fraction dont le numérateur est une somme : terme à terme (« (x + sin x) ÷ x »)
  if (t.k === 'div' && t.a.k === 'add') {
    const parts = t.a.c.map((c): Arg | null => {
      const piece = { k: 'div', a: c, b: t.b } as Term;
      const kp = knownLimit(piece, P, { ...opts, bnd: true });
      const v = isDet(kp) ? kp : (rationalLimit(piece, P) ?? asymLimit(piece, P));
      return v ? { r: v, c: false } : null;
    });
    if (parts.every((p) => p)) {
      const sum = addL(parts as Arg[]);
      if (isDet(sum)) return { v: withSide(sum), via: 'known' };
    }
  }
  const r = rationalLimit(t, P);
  if (r) return { v: withSide(r), via: 'rational' };
  const a = asymLimit(t, P);
  if (a) return { v: withSide(a), via: 'asym' };
  // une différence de racines : l'expression conjuguée
  const cj = conjugateOf(t);
  if (cj) {
    const c = asymLimit(cj, P) ?? rationalLimit(cj, P);
    if (c) return { v: withSide(c), via: 'asym' };
  }
  if (k.k === 'bnd') return { v: k, via: 'known' };
  const n = numericLimit(t, P);
  if (n) return { v: withSide(n), via: 'numeric' };
  return { v: null, via: null };
}

/* ------------------------------ égalités, inégalités ------------------------------ */

/** deux écritures égales près du point (exactement, sinon en 7 valeurs au moins 5 définies) */
export function sameNear(a: Term, b: Term, P: LimPoint): boolean {
  // arctan : le calcul exact parle en degrés, les limites en radians
  if (!hasArc(a) && !hasArc(b))
    try {
      if (compareTerms(a, b) === 'equal') return true;
    } catch {
      /* on compare les valeurs */
    }
  const va = valuesNear(a, P, true);
  const vb = valuesNear(b, P, true);
  let n = 0;
  for (let i = 0; i < va.length; i++) {
    const x = va[i]!.v;
    const y = vb[i]!.v;
    if (Number.isNaN(x) && Number.isNaN(y)) continue;
    if (Number.isNaN(x) || Number.isNaN(y)) return false;
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      if (x !== y) return false;
      continue;
    }
    if (!closeNum(x, y) && Math.abs(x - y) > 1e-9 * Math.max(1, Math.abs(x), Math.abs(y)))
      return false;
    n++;
  }
  return n >= 5;
}

/** une inégalité vraie près du point (toutes les valeurs, au moins 5) */
export function holdsNear(
  l: Term,
  rel: '<' | '>' | '≤' | '≥',
  r: Term,
  P: LimPoint,
): boolean | null {
  const pts = approachPoints(P, true);
  const more = P.seq ? pts.flatMap((p) => [p, p + 1, p + 2]) : pts;
  let n = 0;
  for (const p of more) {
    const x = safeEval(l, { [P.x]: p });
    const y = safeEval(r, { [P.x]: p });
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    const tol = 1e-12 * Math.max(1, Math.abs(x), Math.abs(y));
    const ok =
      rel === '<'
        ? x < y + tol
        : rel === '>'
          ? x > y - tol
          : rel === '≤'
            ? x <= y + tol
            : x >= y - tol;
    if (!ok) return false;
    n++;
  }
  return n >= 5 ? true : null;
}
